import { css } from 'styled-components';

import {
  ManualCSSTheme,
  MediaManualCSS,
  TShirtSizeBreakpointName,
  TShirtSizeBreakpoints,
} from 'app/shared/theme/manualCSS/typings';

// breakpoints define the minimum width of a device (>=)
const breakPoints: TShirtSizeBreakpoints = {
  xs: 0,
  sm: 481,
  md: 768,
  lg: 901,
  xl: 1601,
};
const media = Object.keys(breakPoints).reduce((acc, label: string) => {
  acc[label as keyof TShirtSizeBreakpoints] = (...args: any) => css`
      @media (min-width: ${breakPoints[label as TShirtSizeBreakpointName]}px) {
        ${css.apply(undefined, args)};
      }
    `;
  return acc;
}, {} as MediaManualCSS);

export const manualCSSTheme: ManualCSSTheme = {
  name: 'manualCSS',
  globalStyleName: 'ManualCSSGlobalStyle',

  isFanFacing: true,

  breakPoints,
  media,

  fonts: {
    regular: 'futura-pt, sans-serif',
    primaryFallback: 'Georgia, serif',
  },

  colors: {
    primary: '#D1A03F',
    primaryActive: '#DFBD79',
    primaryHover: '#DFBD79',
    primaryText: '#1C1C1C',
    secondaryText: '#1C1C1C',
    invertedSecondaryText: '#D1A03F',
    disabledText: '#1C1C1C',
    disabledBackground: '#8D8D8D',
    darkBorder: '#1C1C1C',
    focusedBackground: '#DDBEE026',

    // blacks & greys
    black: '#1C1C1C',
    backToBlack: '#1C1C1C',
    blackBetty: '#1C1C1C',
    paintItBlack: '#333333',
    darkSmoke: '#A29992',
    blueSmoke: '#949494',
    lightBlueSmoke: '#A29992',
    macyGrey: '#E5E5E5',
    darkGrey: '#A29992',
    silverSprings: '#E5E5E580',
    silverSpringsForModal: '#F5F5F5',
    whiteDenim: '#FFFFFF',
    warmGrey: '#A29992',

    // green variations
    green900: '#D1A03F',
    green800: '#D1A03F',
    green700: '#D1A03F',
    green600: '#D1A03F',
    green500: '#D1A03F',
    green400: '#D1A03F',
    green300: '#D2F5F2',
    green200: '#D1A03F',
    green100: '#D1A03F',
    green50: '#D1A03F',

    // for colored circles in detail modals
    coloredCircleGrey: '#ebedef',
    coloredCircleBlue: '#ebf5fb',
    coloredCircleGreen: '#f3f9ed',

    // additional
    greenOnions: '#DFBD79',
    greenGarden: '#D1A03F',
    blueChristmas: '#D1A03F',
    purpleHaze: '#D1A03F',
    purplePills: '#D1A03F',
    checkBerry: '#FF8080',
    blueMagic: '#A29992',
    blueGrey: '#a7bdd4',
    brownSugar: '#9B525F',
    pinkTriangle: '#FF8080',
    royalBlood: '#FF8080',
    redRedWine: '#A60000',
    peaches: '#F99973',
    orangeCrush: '#FEC907',
    darkOrange: '#BF6900',
    mellowYellow: '#FEC907',
    yellowSubmarine: '#FEC907',
  },
  fontSizes: {
    base: { lg: '16px', xs: '16px' },
    title: { lg: '48px', md: '40px', xs: '40px' },
    h1: { lg: '48px', md: '40px', xs: '40px' },
    h2: { lg: '40px', md: '32px', xs: '32px' },
    h3: { lg: '32px', md: '28px', xs: '28px' },
    h4: { lg: '24px', md: '24px', xs: '24px' },
    h5: { lg: '20px', md: '20px', xs: '20px' },
    h6: { lg: '18px', md: '18px', xs: '18px' },
    body1: { lg: '16px', xs: '16px' },
    body2: { lg: '14px', xs: '14px' },
    overline: { lg: '14px', xs: '14px' },
    caption: { lg: '14px', xs: '14px' },
    button: { lg: '16px', xs: '16px' },
    formGroupLabel: '16px',
    tooltip: '10px',
    selectOption: '14px',
  },

  fontColors: {
    primary: '#1C1C1C',
    secondary: '#333333',
  },

  fontStyles: {
    links: {
      textDecoration: 'none!important',
      fontWeight: 'bold',
      fontFamily: 'futura-pt, sans-serif',
    },
    title: {
      letterSpacing: '2px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h1: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '2px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h2: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '0px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h3: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '2px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h4: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '0px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h5: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '0px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    h6: {
      lineHeight: '120%',
      textTransform: 'none',
      letterSpacing: '2px',
      fontFamily: 'bely-display, Georgia, serif',
    },
    body: {
      lineHeight: '140%',
      letterSpacing: '0.5px',
    },
    body2: {
      lineHeight: '140%',
      letterSpacing: '0.5px',
    },
    caption: {
      lineHeight: '120%',
      letterSpacing: '0.5px',
    },
    formGroupLabel: {
      letterSpacing: '0.5px',
    },
    button: {
      fontWeight: '700',
      letterSpacing: '2px',
      lineHeight: '100%',
    },
  },

  ruler: Array.from({ length: 28 }, (_e, i) => i * 4),

  zIndex: {
    navbar: 100,
    genericSmallModal: 100,
    routableModal: 100,
    snackbar: 101, // Needs to be higher than modals and their content
    toolTip: 1,
    venueMapNameToolTip: 33,
  },

  dimensions: {
    buttonHeight: '40px',
    radioBox: '20px',
    containerWidth: {
      small: '600px',
      default: '800px',
      large: '1000px',
      fullscreen: '100%',
    },

    button: {
      small: {
        height: 'auto',
        padding: '9px 15px',
      },

      default: {
        height: '40px',
        padding: '12px 24px',
      },
    },

    navbarHeight: {
      lg: '88px',
      md: '130px',
      xs: '102px',
      xsNoCitySelector: '60px',
      mdNoCitySelector: '60px',
    },

    container: {
      padding: '0 16px',
      paddingSm: '0 32px',
      maxWidthSm: '1176px',
      maxWidthMd: '1176px',
      maxWidthLg: '1176px',
      maxWidthXl: '1176px',
      innerMargin: '0',
    },

    menuItem: {
      padding: '10px 16px',
    },

    input: {
      height: '44px',
    },
  },

  borderRadius: {
    default: '0',
    large: '0',
    button: '60px',
    buttonSmall: '60px',
    input: '0',
  },

  utils: {
    transition: (attr?: string) => `${attr || 'all'} .2s ease`,
    getMediaQuery: (breakpointKey: string) => {
      if (breakpointKey === 'xs') {
        return css;
      } else if (
        media.hasOwnProperty(breakpointKey as TShirtSizeBreakpointName)
      ) {
        return media[breakpointKey as TShirtSizeBreakpointName];
      } else {
        throw new Error(`Breakpoint ${breakpointKey} not found`);
      }
    },
  },
};
